import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import TabPanel from "../../../../../../../../shared/UI/TabPanel";

import { AddEntityLocationsFormData } from "../../../../../../types/editEntityProfile-type";

import BranchDetailsTabs, { BasicInfoTab, WorkingHoursTab, SportsTab, FacilitiesTab } from "./BranchDetailsTabs";

const TABS = [
  {
    value: "basic-info",
    component: (locationIdx: number, branchIdx: number) => (
      <BasicInfoTab locationIdx={locationIdx} branchIdx={branchIdx} />
    ),
  },
  {
    value: "working-hours",
    component: (locationIdx: number, branchIdx: number) => (
      <WorkingHoursTab locationIdx={locationIdx} branchIdx={branchIdx} />
    ),
  },
  {
    value: "sports",
    component: (locationIdx: number, branchIdx: number) => (
      <SportsTab locationIdx={locationIdx} branchIdx={branchIdx} />
    ),
  },
  {
    value: "facilities",
    component: (locationIdx: number, branchIdx: number) => (
      <FacilitiesTab locationIdx={locationIdx} branchIdx={branchIdx} />
    ),
  },
] as const;

type TabType = (typeof TABS)[number]["value"];

const BranchDetails = ({ locationIdx, branchIdx }: { locationIdx: number; branchIdx: number }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const [activeTab, setActiveTab] = useState<TabType>("basic-info");
  const {
    formState: { errors },
  } = useFormContext<AddEntityLocationsFormData>();

  useEffect(() => {
    const locationErrors = errors.locations?.[locationIdx]?.branches?.[branchIdx];
    if (locationErrors) {
      if (locationErrors.branchName || locationErrors.branchAddress || locationErrors.branchGoogleMapsAddress) {
        setActiveTab("basic-info");
        return;
      }
      if (locationErrors.workingHours) {
        setActiveTab("working-hours");
        return;
      }
      if (locationErrors.sports) {
        setActiveTab("sports");
        return;
      }
      if (locationErrors.facilities) {
        setActiveTab("facilities");
      }
    }
  }, [errors, locationIdx, branchIdx]);

  return (
    <Box component="div" sx={styles.container}>
      <Box component="div" sx={styles.headerContainer}>
        <Typography component="div" sx={styles.title}>
          {t("addBranch")}
        </Typography>
        <Typography component="div" sx={styles.subtitle}>
          {t("enterBranchDetails")}
        </Typography>
      </Box>
      <BranchDetailsTabs activeTab={activeTab} onTabChange={setActiveTab} />
      {TABS.map((tab) => (
        <TabPanel key={tab.value} value={activeTab} tab={tab.value}>
          {tab.component(locationIdx, branchIdx)}
        </TabPanel>
      ))}
    </Box>
  );
};

export default BranchDetails;

const styles = {
  container: {
    borderRadius: "10px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "#FAFAFA",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "16px",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  subtitle: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
};
